import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { syncTransactions, updateAccountNickname, disconnectAccount } from "@/api/banking";
import { getRecurringTransactions } from "@/api/forecasting";
import { getTransactions } from "@/api/transactions";
import { bankAccountsQuery } from "@/lib/appQueries";

export function useBankAccounts() {
  return useQuery(bankAccountsQuery);
}

/**
 * True only when the accounts list has LOADED and is empty — the user has no
 * bank connected (BEN-141, the "Skip for now" path).
 *
 * Deliberately false while loading and on error. Loading: the list is warm cache
 * on every authed route (AppLayout fetches it), and a connected user — the common
 * case — must never see a page held back behind it. Error: a failed fetch says
 * nothing about whether a bank exists, so it must not be answered with "connect
 * a bank"; each page's own error state covers that.
 */
export function useNoBankConnected(): boolean {
  const { isSuccess, data } = useBankAccounts();
  return isSuccess && Array.isArray(data) && data.length === 0;
}

/** Keys for the two "does this user have any of X" probes below. The transactions
 *  key sits under ["transactions"], which syncs and categorisation writes already
 *  invalidate; the recurring key is in use-financial-analysis's period-scoped set.
 *  The /recurring page writes by plain fetch and invalidates nothing, so the probes
 *  also run with staleTime 0: a page that mounts after an edit asks again rather
 *  than trusting a verdict from before it (found in review). */
export const ANY_TRANSACTIONS_KEY = ["transactions", "any"] as const;
export const ANY_RECURRING_KEY = ["recurring", "any"] as const;

/**
 * "No bank" is not "no data" (DLT-W-02). `useNoBankConnected` is true for anyone
 * with zero ACTIVE accounts, which includes a user who disconnected their last
 * bank (whose transactions are kept, as the Disconnect dialog promises) and a
 * user who typed recurring income and bills into /recurring. Gating a page on
 * it alone hid that user's history, CSV export included, and their forecast.
 *
 * These gate on "no bank AND none of this page's own data". They probe only
 * when there is no bank, so a connected user never pays for the extra request.
 * Until the probe answers the gate stays shut, which is what it did before, so
 * a user with no data never sees the page's zeros flash first.
 */
export function useNoBankAndNoTransactions(): boolean {
  const noBank = useNoBankConnected();
  const probe = useQuery({
    queryKey: ANY_TRANSACTIONS_KEY,
    queryFn: () => getTransactions({ page_size: 1 }),
    enabled: noBank,
    staleTime: 0,
  });
  return noBank && !(probe.isSuccess && probe.data.count > 0);
}

/**
 * FP-035 (web adopts #1728): how many DETECTED bills the user has excluded, i.e.
 * inactive, non-income streams with a Plaid `stream_id`. The analysis response doesn't
 * carry it. Under ["recurring"], so every recurring edit invalidates it.
 */
export function useExcludedDetectedBillCount(): number {
  const q = useQuery({
    queryKey: ["recurring", "excluded-detected-bills"],
    queryFn: getRecurringTransactions,
  });
  return (q.data ?? []).filter((r) => !r.is_active && !r.is_income && r.stream_id != null).length;
}

/** The same, for pages built from recurring income and bills (Forecast, Calendar). */
export function useNoBankAndNoRecurring(): boolean {
  const noBank = useNoBankConnected();
  const probe = useQuery({
    queryKey: ANY_RECURRING_KEY,
    queryFn: getRecurringTransactions,
    enabled: noBank,
    staleTime: 0,
  });
  return noBank && !(probe.isSuccess && probe.data.some((row) => row.is_active));
}

export function useSyncTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncTransactions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["transactionSummary"] });
      // Report 145: a sync can move `bank_unreachable` (a bank flagged, a paycheck back).
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

export function useUpdateAccountNickname() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, nickname }: { id: number; nickname: string }) =>
      updateAccountNickname(id, nickname),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
    },
  });
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => disconnectAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
    },
  });
}
